import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Mail, Linkedin, Github, Twitter } from 'lucide-react';
import { portfolioData } from '../data/portfolio';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { profile } = portfolioData;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'My Footprint', path: '/my-footprint' },
    { label: 'Projects', path: '/projects' },
    { label: 'Blog', path: '/blog' }
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#0a0a0a]/95 backdrop-blur-sm border-b border-white/10' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Left Side - Logo & Navigation */}
          <div className="flex items-center gap-8">
            {/* Logo */}
            <Link
              to="/"
              className="text-2xl font-bold text-white hover:text-[#00b4d8] transition-colors"
            >
              {profile.logo}
            </Link>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'text-[#00b4d8]'
                      : 'text-[#a0a0a0] hover:text-[#00b4d8]'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Right Side - Social Icons & Mobile Menu */}
          <div className="flex items-center gap-4">
            {/* Social Icons - Desktop */}
            <div className="hidden md:flex items-center gap-3">
              <a
                href={`mailto:${profile.email}`}
                className="text-[#a0a0a0] hover:text-[#00b4d8] transition-colors"
                title="Email"
              >
                <Mail size={20} />
              </a>
              <a
                href={profile.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#a0a0a0] hover:text-[#00b4d8] transition-colors"
                title="LinkedIn"
              >
                <Linkedin size={20} />
              </a>
              <a
                href={profile.social.github}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#a0a0a0] hover:text-[#00b4d8] transition-colors"
                title="GitHub"
              >
                <Github size={20} />
              </a>
              <a
                href={profile.social.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#a0a0a0] hover:text-[#00b4d8] transition-colors"
                title="Twitter"
              >
                <Twitter size={20} />
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden text-white hover:text-[#00b4d8] transition-colors"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Overlay */}
        {isMobileMenuOpen && (
          <div 
            className="md:hidden fixed inset-0 top-0 bg-[#0a0a0a]/95 backdrop-blur-sm z-40"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
        
        {/* Mobile Navigation Menu */}
        <div 
          className={`md:hidden fixed left-0 right-0 top-[62px] bg-[#0a0a0a] border-b border-white/10 px-6 shadow-xl overflow-hidden transition-all duration-300 ease-in-out z-50 ${
            isMobileMenuOpen ? 'max-h-80 py-4 opacity-100' : 'max-h-0 py-0 opacity-0 border-b-0'
          }`}
        >
          <div className="space-y-3">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block w-full text-left text-sm font-medium py-2 transition-colors ${
                  isActive(item.path)
                    ? 'text-[#00b4d8]'
                    : 'text-[#a0a0a0] hover:text-[#00b4d8]'
                }`}
              >
                {item.label}
              </Link>
            ))}
            
            {/* Mobile Social Links */}
            <div className="flex items-center gap-4 pt-4 border-t border-white/10">
              <a
                href={`mailto:${profile.email}`}
                className="text-[#a0a0a0] hover:text-[#00b4d8] transition-colors"
              >
                <Mail size={20} />
              </a>
              <a
                href={profile.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#a0a0a0] hover:text-[#00b4d8] transition-colors"
              >
                <Linkedin size={20} />
              </a>
              <a
                href={profile.social.github}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#a0a0a0] hover:text-[#00b4d8] transition-colors"
              >
                <Github size={20} />
              </a>
              <a
                href={profile.social.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#a0a0a0] hover:text-[#00b4d8] transition-colors"
              >
                <Twitter size={20} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
